import { useHandler } from "@redotech/react-util/hook";
import { SchemaField } from "@redotech/redo-flows-core-schema-common/type-system/schema";
import { useEffect, useMemo, useState } from "react";
import {
  detectInlineVariableQuery,
  InlineVariableTrigger,
  isInsertableVariable,
  matchesVariableQuery,
} from "./inline-variable-autocomplete";

/**
 * Drives the inline variable menu for one text surface: the surface says how to
 * read its caret and replace the typed query, the hook owns everything else.
 */
export function useInlineVariableAutocomplete({
  enabled,
  target,
  variables,
  textBeforeCaret,
  replaceQuery,
  triggers,
}: {
  enabled: boolean;
  target: HTMLElement | null;
  variables: readonly SchemaField[];
  textBeforeCaret: () => string | null;
  replaceQuery: (startIndex: number, field: SchemaField) => void;
  /** Defaults to every trigger. Narrow it where one is already spoken for. */
  triggers?: readonly InlineVariableTrigger[];
}) {
  const [query, setQuery] = useState<{
    startIndex: number;
    query: string;
  } | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const fields = useMemo(
    () =>
      query
        ? variables
            .filter(isInsertableVariable)
            .filter((field) => matchesVariableQuery(field, query.query))
            .toSorted((a, b) => a.key.localeCompare(b.key))
        : [],
    [variables, query],
  );

  const clampedActiveIndex =
    fields.length === 0 ? 0 : Math.min(activeIndex, fields.length - 1);
  const open = enabled && !!query;

  const close = useHandler(() => setQuery(null));

  const select = useHandler((field: SchemaField) => {
    if (query) {
      replaceQuery(query.startIndex, field);
      setQuery(null);
    }
  });

  const refresh = useHandler(() => {
    const before = textBeforeCaret();
    setQuery(
      before == null ? null : detectInlineVariableQuery(before, triggers),
    );
  });

  useEffect(() => {
    if (!enabled || !target) {
      return;
    }
    for (const event of ["input", "click", "keyup"]) {
      target.addEventListener(event, refresh);
    }
    return () => {
      for (const event of ["input", "click", "keyup"]) {
        target.removeEventListener(event, refresh);
      }
    };
  }, [enabled, target, refresh]);

  useEffect(() => {
    if (!open || !target || fields.length === 0) {
      return;
    }
    // Capture phase, to beat the surface's own Enter and arrow handling.
    const onKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case "ArrowDown": {
          event.preventDefault();
          setActiveIndex((index) => (index + 1) % fields.length);
          break;
        }
        case "ArrowUp": {
          event.preventDefault();
          setActiveIndex(
            (index) => (index - 1 + fields.length) % fields.length,
          );
          break;
        }
        case "Enter":
        case "Tab": {
          const field = fields[clampedActiveIndex];
          if (field) {
            event.preventDefault();
            event.stopPropagation();
            select(field);
          }
          break;
        }
        case "Escape": {
          event.preventDefault();
          close();
          break;
        }
        // No default
      }
    };
    target.addEventListener("keydown", onKeyDown, true);
    return () => target.removeEventListener("keydown", onKeyDown, true);
  }, [open, target, fields, clampedActiveIndex, select, close]);

  return {
    open: open && fields.length > 0,
    fields,
    activeIndex: clampedActiveIndex,
    setActiveIndex,
    select,
    close,
  };
}
